import { useState } from 'react';
import { ChevronDown, Rocket, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { useGate } from '@/client/hooks/useGate';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useSetAppsCatalogAppStatusMutation } from '@/client/api/apps-catalog/apps-catalog.mutation';
import type {
  AdminCatalogApp,
  AppCatalogStatus,
  AppsCatalogMeta,
} from '@/client/types/apps-catalog';
import {
  APPS_CATALOG_PERMS,
  STATUS_LABELS,
  isRoutedKey,
  showCatalogWarnings,
  statusLabel,
  unroutedPublishWarning,
} from './catalogUi';

const STATUS_EFFECT: Record<AppCatalogStatus, string> = {
  published:
    'The app is listed in the public catalog on its sites, in the header grid and the store as its flags allow.',
  coming_soon: 'The app is listed in the public catalog, marked as coming soon.',
  draft: 'The app leaves the public catalog. Only admin and the preview can see it.',
  unpublished: 'The app leaves the public catalog. Only admin and the preview can see it.',
};

interface AppStatusControlProps {
  app: Pick<AdminCatalogApp, 'app_key' | 'name' | 'status'>;
  meta?: AppsCatalogMeta;
  size?: 'sm' | 'default';
}

/**
 * Status change, kept apart from the edit form: it needs apps.catalog.publish
 * and always goes through a confirmation. Publishing a key ff-ui has no route
 * for warns inside the dialog, and any API `warnings` surface as toasts.
 */
export const AppStatusControl = ({ app, meta, size = 'sm' }: AppStatusControlProps) => {
  const gate = useGate(APPS_CATALOG_PERMS.publish);
  const mutation = useSetAppsCatalogAppStatusMutation();
  const [target, setTarget] = useState<AppCatalogStatus | null>(null);

  const statuses: AppCatalogStatus[] = meta?.statuses?.length
    ? meta.statuses
    : (Object.keys(STATUS_LABELS) as AppCatalogStatus[]);
  const warnUnrouted = target === 'published' && !isRoutedKey(meta, app.app_key);

  const confirm = async () => {
    if (!target) return;
    const toastId = toast.loading('Changing status…');
    try {
      const result = await mutation.mutateAsync({ app_key: app.app_key, status: target });
      if (result.changed) {
        toast.success(`${app.name} is now ${statusLabel(target)}`, { id: toastId });
      } else {
        toast.info(`${app.name} was already ${statusLabel(target)}`, { id: toastId });
      }
      showCatalogWarnings(result.warnings);
      setTarget(null);
    } catch (error) {
      toast.error('Status change failed', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
      });
    }
  };

  if (!gate.allowed) {
    return (
      <PermissionButton code={APPS_CATALOG_PERMS.publish} size={size} variant="outline" className="gap-1.5">
        <Rocket className="h-3.5 w-3.5" />
        Status
      </PermissionButton>
    );
  }

  return (
    <>
      {/* Non-modal so the confirmation dialog can take focus after an item is picked. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button size={size} variant="outline" className="gap-1.5" disabled={mutation.isPending}>
            <Rocket className="h-3.5 w-3.5" />
            Status
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Change status</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {statuses.map((status) => (
            <DropdownMenuItem
              key={status}
              disabled={status === app.status}
              onSelect={() => setTarget(status)}
            >
              {statusLabel(status)}
              {status === app.status ? ' (current)' : ''}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog
        open={target != null}
        onOpenChange={(open) => {
          if (!open && !mutation.isPending) setTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change {app.name} from {statusLabel(app.status)} to {target ? statusLabel(target) : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {target ? STATUS_EFFECT[target] : ''} Public reads update at once; browser and CDN
              copies can take up to 5 minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {warnUnrouted && (
            <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <p>{unroutedPublishWarning(app.app_key)}</p>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirm();
              }}
              disabled={mutation.isPending}
              className={warnUnrouted ? 'bg-amber-600 text-white hover:bg-amber-700' : undefined}
            >
              {mutation.isPending
                ? 'Saving…'
                : warnUnrouted
                  ? 'Publish anyway'
                  : `Change to ${target ? statusLabel(target) : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AppStatusControl;
